/**
 * "A new version is ready" (task 14.2).
 *
 * A banner rather than a dialog, and pinned to the bottom rather than dropped
 * over the top. The one moment this appears that matters is mid-mission, and a
 * modal there would take the screen away from a run the player is in the middle
 * of — which is the exact interruption the spec is written to prevent.
 *
 * "Later" is a real answer. The waiting build stays waiting; the prompt does
 * not come back until the next one installs, and the player can keep playing on
 * the version they started the mission on.
 *
 * Accepting flushes the run to disk before the page is replaced. That happens
 * in the view store's `applyUpdate`, not here — this only has to say what is
 * about to happen and not lie about it.
 */
import type { JSX } from 'react';
import { Button } from './Button.js';
import { useViewStore } from '../store/view-store.js';

export function UpdatePrompt(): JSX.Element | null {
  const update = useViewStore((state) => state.update);
  const applyUpdate = useViewStore((state) => state.applyUpdate);
  const dismissUpdate = useViewStore((state) => state.dismissUpdate);
  const hasRun = useViewStore((state) => state.doc !== null);

  if (!update.ready) return null;

  return (
    <div
      role="status"
      data-testid="update-prompt"
      className="pb-safe px-safe fixed inset-x-0 bottom-0 z-40 flex justify-center p-3"
    >
      <div className="flex w-full max-w-md flex-col gap-2 rounded-xl border border-line bg-surface p-4 shadow-card">
        <p className="text-sm font-semibold text-ink">A new version of the game is ready.</p>
        <p className="text-xs text-ink-muted">
          {hasRun
            ? 'Reloading the page takes a few seconds. Your run is saved first, so you continue from the same place.'
            : 'Reloading the page takes a few seconds.'}
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="ghost"
            disabled={update.applying}
            data-testid="update-later"
            onClick={dismissUpdate}
          >
            Later
          </Button>
          <Button
            variant="primary"
            disabled={update.applying}
            data-testid="update-apply"
            onClick={() => {
              void applyUpdate();
            }}
          >
            {update.applying ? 'Saving…' : 'Reload'}
          </Button>
        </div>
      </div>
    </div>
  );
}
